import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

import useFilterStore from "@/stores/useFilterStore";

import CustomButton from "@/components/desktop/button/CustomButton.jsx";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";
import { getOAs } from "@/api/Dashboard/dashboard";
import Fetching from "@/components/Fetching/Fetching";
import OAChart from "@/pages/Dashboard/Cards/OA/components/OAChart/OAChart.jsx";

import { ORDER_TYPES, INITIAL_PARAMS } from "./dashboard-oa.constants";
import classes from "./dashboard-oa.module.scss";

const DashboardOA = ({ filters }) => {
	const navigate = useNavigate();
	const { estate_id } = useFilterStore((state) => state.filter);

	const [formParams, setFormParams] = useState(INITIAL_PARAMS);
	const [apiParams, setApiParams] = useState(INITIAL_PARAMS);

	const { data, isLoading, isError } = useQuery(
		[
			"application_orders",
			{
				...filters,
				estate_id,
				...apiParams,
			},
		],
		() =>
			getOAs({
				...filters,
				estate_id,
				...apiParams,
			}),
	);

	useEffect(() => {
		setFormParams(INITIAL_PARAMS);
		setApiParams(INITIAL_PARAMS);
	}, [estate_id]);

	const availableVarieties = useMemo(() => {
		if (!data?.filters?.crops_detail) return [];
		if (formParams.crops.length === 0) return data.filters.crops_detail;
		return data.filters.crops_detail.filter((option) => formParams.crops.includes(option.crop_id));
	}, [data?.filters?.crops_detail, formParams.crops]);

	const availableBusinessCenters = useMemo(() => {
		if (!data?.filters?.business_centers) return [];
		if (formParams.crops_detail.length > 0) {
			return data.filters.business_centers.filter((option) => option.crop_detail_id && formParams.crops_detail.includes(option.crop_detail_id));
		}
		if (formParams.crops.length > 0) {
			return data.filters.business_centers.filter((option) => option.crop_id && formParams.crops.includes(option.crop_id));
		}
		return data.filters.business_centers;
	}, [data?.filters?.business_centers, formParams.crops, formParams.crops_detail]);

	const transformChartData = (chartData) => {
		return (chartData?.data || []).map((item) => ({
			label: item.status_name,
			value: item.count,
			fill: item.color,
			status_id: item.status_id,
			oa_types: formParams.oa_types,
		}));
	};

	const handle = (clickEvent) => {
		if (clickEvent && clickEvent.activePayload && clickEvent.activePayload.length > 0) {
			const clickedData = clickEvent.activePayload[0].payload;
			const params = [];

			if (clickedData.status_id) {
				params.push(`status=${clickedData.status_id}`);
			}
			if (clickedData.oa_types) {
				params.push(`type=${clickedData.oa_types.join(",")}`);
			}

			navigate(`/ordenes_aplicacion?${params.join("&")}`);
		}
	};

	const handleFilterChange = (filterName, value) => {
		let newParams = { ...formParams, [filterName]: value };

		if (filterName === "crops") {
			const validVarietyIDs = (data?.filters?.crops_detail || []).filter((option) => value.includes(option.crop_id)).map((option) => option.value);
			newParams.crops_detail = newParams.crops_detail.filter((id) => validVarietyIDs.includes(id));
			newParams.business_centers = [];
		}

		if (filterName === "crops_detail") {
			newParams.business_centers = [];
		}

		setFormParams(newParams);
	};

	const handleApplyFilter = () => {
		setApiParams(formParams);
	};

	const isIrrigationSectorEnabled = formParams.oa_types.includes(ORDER_TYPES.IRRIGATION);
	const isCostSectorEnabled = formParams.oa_types.length > 1 || !formParams.oa_types.includes(ORDER_TYPES.IRRIGATION);

	const chartData = data ? transformChartData(data) : [];

	if (isLoading || !data || isError) {
		return <Fetching topMargin="5em" bottomMargin="5em" />;
	}

	return (
		<div>
			<div className={classes.container}>
				<div className={classes.show_table}>
					<span>{data?.total ?? 0} Órdenes</span>
				</div>
				<div className={classes.filters}>
					<div>
						<CustomInputSelect
							size={"sm"}
							label="Tipo de Orden"
							name="order_type"
							options={data?.filters?.oa_types ?? []}
							value={formParams.oa_types}
							onChange={(value) => handleFilterChange("oa_types", value)}
							placeholder="Tipo de Orden"
							isMulti={true}
							minimal={true}
							isClearable={true}
						/>
					</div>
					<div>
						<CustomInputSelect
							size={"sm"}
							label="Especie"
							name="crops"
							options={data?.filters?.crops ?? []}
							value={formParams.crops}
							onChange={(value) => handleFilterChange("crops", value)}
							placeholder="Especie"
							isMulti={true}
							minimal={true}
							isClearable={true}
						/>
					</div>
					<div>
						<CustomInputSelect
							size={"sm"}
							label="Variedad"
							name="crops_detail"
							options={availableVarieties}
							value={formParams.crops_detail}
							onChange={(value) => handleFilterChange("crops_detail", value)}
							placeholder="Variedad"
							isMulti={true}
							minimal={true}
							isClearable={true}
						/>
					</div>
					<div>
						<CustomInputSelect
							size={"sm"}
							label="Centro de Costo"
							name="business_centers"
							options={isCostSectorEnabled ? availableBusinessCenters : []}
							value={isCostSectorEnabled ? formParams.business_centers : []}
							onChange={(value) => handleFilterChange("business_centers", value)}
							placeholder="Centro de Costo"
							isMulti={true}
							disabled={!isCostSectorEnabled}
							minimal={true}
							isClearable={true}
						/>
					</div>
					<div>
						<CustomInputSelect
							size={"sm"}
							label="Sector de riego"
							name="irrigation_sectors"
							options={isIrrigationSectorEnabled ? (data?.filters?.irrigation_sectors ?? []) : []}
							value={isIrrigationSectorEnabled ? formParams.irrigation_sectors : []}
							onChange={(value) => handleFilterChange("irrigation_sectors", value)}
							placeholder="Sector de riego"
							isMulti={true}
							disabled={!isIrrigationSectorEnabled}
							minimal={true}
							isClearable={true}
						/>
					</div>
				</div>

				<div className={classes.filter_button}>
					<CustomButton variant={"primary"} size={"sm"} startIcon={<Search size={16} />} onClick={handleApplyFilter}>
						Filtrar
					</CustomButton>
				</div>
			</div>

			<OAChart chartData={chartData} onChartClick={handle} />
		</div>
	);
};

export default DashboardOA;
